import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { format, parseISO } from "date-fns";
import { Sparkles, MapPin, Calendar as CalIcon, Clock, Heart, BadgeCheck, Star, DollarSign } from "lucide-react";
import { toast } from "sonner";
import RoyalReferralCard from "@/components/RoyalReferralCard";
import VIPStatusCard from "@/components/VIPStatusCard";

const STATUS_COLORS = {
    pending: "bg-yellow-900/40 text-yellow-300 border-yellow-700/40",
    confirmed: "bg-emerald-900/40 text-emerald-300 border-emerald-700/40",
    in_progress: "bg-blue-900/40 text-blue-300 border-blue-700/40",
    completed: "bg-card text-muted-foreground border-white/10",
    cancelled: "bg-red-900/30 text-red-300 border-red-700/40",
};

export default function MyBookingsPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [trusted, setTrusted] = useState([]);
    const [loading, setLoading] = useState(true);

    const trustedIds = trusted.map((t) => t.id);

    const fetchData = () => {
        setLoading(true);
        Promise.all([
            api.get("/bookings").then((r) => setBookings(r.data)),
            api.get("/users/me/trusted").then((r) => setTrusted(r.data)).catch(() => setTrusted([])),
        ]).finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCancel = async (id) => {
        if (!confirm("Cancel this booking? Royal cancellation terms apply (full credit >24h, 50% 2–24h, nothing <2h).")) return;
        try {
            const { data } = await api.post(`/bookings/${id}/cancel`);
            const msg =
                data.tier === "full" ? `Cancelled. Full refund credited as ${data.coins_credited.toLocaleString()} SMART Coins.`
                : data.tier === "half" ? `Cancelled. 50% refund credited (${data.coins_credited.toLocaleString()} SMART Coins).`
                : "Cancelled. No credit — too close to appointment.";
            toast.success(msg);
            fetchData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const handleBookAgain = (b) => {
        // Pre-select the same service — the booking page reads these query params
        // on mount and pre-fills address + hours when present.
        const params = new URLSearchParams({
            address: b.address || "",
            city: b.city || "",
            ...(b.hours ? { hours: String(b.hours) } : {}),
            ...(b.notes ? { notes: b.notes } : {}),
        });
        navigate(`/book/${b.service_id}?${params.toString()}`);
    };

    const toggleTrust = async (providerId, providerName) => {
        const isTrusted = trustedIds.includes(providerId);
        try {
            if (isTrusted) {
                await api.delete(`/users/me/trusted/${providerId}`);
                toast.success(`${providerName} removed from your trusted list`);
            } else {
                await api.post(`/users/me/trusted/${providerId}`);
                toast.success(`${providerName} added to your trusted list ♥`);
            }
            fetchData();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="min-h-screen bg-background" data-testid="my-bookings-page">
            <Header />

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
                    <div>
                        <div className="overline mb-3">Concierge</div>
                        <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter">
                            Hello, <span className="italic gold-grad">{user?.name?.split(" ")[0]}</span>.
                        </h1>
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                        <Stat label="Bookings" value={bookings.length} />
                        <Stat
                            label="Loyalty pts"
                            value={user?.loyalty_points || 0}
                            icon={<Sparkles size={14} className="gold-text" strokeWidth={1.5} />}
                        />
                        <Stat
                            label="Trusted pros"
                            value={trusted.length}
                            icon={<Heart size={14} className="gold-text" strokeWidth={1.5} fill="currentColor" />}
                        />
                    </div>
                </div>

                <div className="mb-12">
                    <VIPStatusCard />
                </div>

                {/* Trusted sitters strip */}
                {trusted.length > 0 && (
                    <div className="mb-12" data-testid="trusted-strip">
                        <div className="overline mb-4 flex items-center gap-2">
                            <Heart size={12} fill="currentColor" /> Your Trusted Roster
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {trusted.map((p) => (
                                <div
                                    key={p.id}
                                    className="flex items-center gap-3 px-4 py-2.5 bg-card border border-primary/30 rounded-sm"
                                    data-testid={`trusted-chip-${p.id}`}
                                >
                                    <BadgeCheck size={14} strokeWidth={1.5} className="gold-text" />
                                    <div>
                                        <div className="text-sm">{p.name}</div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            {(p.service_categories || []).join(" · ")}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => toggleTrust(p.id, p.name)}
                                        className="ml-2 text-primary hover:text-red-400 transition-colors"
                                        data-testid={`untrust-${p.id}`}
                                        aria-label="Remove from trusted"
                                    >
                                        <Heart size={14} strokeWidth={1.5} fill="currentColor" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="space-y-4">
                        {Array.from({ length: 3 }, (_, i) => (
                            <div key={`bookings-skeleton-${i}`} className="h-40 bg-card border border-white/5 animate-pulse" />
                        ))}
                    </div>
                ) : bookings.length === 0 ? (
                    <div className="text-center py-24 border border-white/5 bg-card" data-testid="empty-bookings">
                        <div className="overline mb-4">No bookings yet</div>
                        <h3 className="font-serif text-3xl mb-6">Start your first concierge moment.</h3>
                        <Button
                            onClick={() => navigate("/services")}
                            className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7"
                            data-testid="empty-bookings-cta"
                        >
                            Browse Services
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="bookings-list">
                        {bookings.map((b) => {
                            const isProviderTrusted = b.provider_id && trustedIds.includes(b.provider_id);
                            const showHeart = !!b.provider_id;
                            const isCompleted = b.status === "completed";
                            const canReview = isCompleted && !b.rating && b.provider_id;
                            return (
                                <article
                                    key={b.id}
                                    className="grid lg:grid-cols-12 gap-6 p-6 lg:p-8 bg-card border border-white/5 hover:border-primary/30 transition-all"
                                    data-testid={`booking-row-${b.id}`}
                                >
                                    <div className="lg:col-span-5">
                                        <div className="overline mb-2">{b.service_category}</div>
                                        <h3 className="font-serif text-2xl leading-tight">{b.service_name}</h3>
                                        {b.provider_name && (
                                            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                                                <span>Pro · {b.provider_name}</span>
                                                {isProviderTrusted && (
                                                    <span className="inline-flex items-center gap-1 gold-text">
                                                        <Heart size={10} fill="currentColor" strokeWidth={1.5} /> trusted
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {b.notes && <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{b.notes}</p>}
                                    </div>
                                    <div className="lg:col-span-4 space-y-2 text-sm text-muted-foreground">
                                        <div className="flex items-center gap-2">
                                            <CalIcon size={14} strokeWidth={1.5} />
                                            {format(parseISO(b.scheduled_at), "EEE, MMM d · HH:mm")}
                                            {b.hours && (
                                                <span className="ml-1 text-xs gold-text">· {b.hours}h</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <MapPin size={14} strokeWidth={1.5} /> {b.city}
                                            {b.distance_km != null && b.eta_minutes != null && (
                                                <span
                                                    className="ml-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] gold-text border border-primary/30 rounded-sm"
                                                    data-testid={`eta-chip-${b.id}`}
                                                    title="Live distance & drive time from dispatch"
                                                >
                                                    {b.distance_km}km · {b.eta_minutes}min ETA
                                                </span>
                                            )}
                                        </div>
                                        {b.express && (
                                            <div className="flex items-center gap-2 gold-text">
                                                <Clock size={14} strokeWidth={1.5} /> Express
                                            </div>
                                        )}
                                        {b.overnight && (
                                            <div className="flex items-center gap-2 gold-text text-xs uppercase tracking-[0.2em]">
                                                Overnight care
                                            </div>
                                        )}
                                    </div>
                                    <div className="lg:col-span-3 flex flex-col items-start lg:items-end justify-between gap-4">
                                        <div className="flex flex-wrap gap-2 items-center">
                                            <Badge
                                                className={`uppercase text-[10px] tracking-[0.2em] rounded-sm border ${STATUS_COLORS[b.status] || ""}`}
                                                data-testid={`status-badge-${b.id}`}
                                            >
                                                {b.status.replace("_", " ")}
                                            </Badge>
                                            <Badge
                                                variant="outline"
                                                className={`uppercase text-[10px] tracking-[0.2em] rounded-sm border ${
                                                    b.payment_status === "paid"
                                                        ? "border-emerald-700/40 text-emerald-300"
                                                        : "border-white/10 text-muted-foreground"
                                                }`}
                                                data-testid={`payment-badge-${b.id}`}
                                            >
                                                {b.payment_status}
                                            </Badge>
                                            {showHeart && (
                                                <button
                                                    onClick={() => toggleTrust(b.provider_id, b.provider_name)}
                                                    className={`p-1.5 rounded-sm border transition-all ${
                                                        isProviderTrusted
                                                            ? "border-primary text-primary bg-primary/10"
                                                            : "border-white/10 text-muted-foreground hover:text-primary hover:border-primary/40"
                                                    }`}
                                                    data-testid={`trust-toggle-${b.id}`}
                                                    aria-label={isProviderTrusted ? "Remove from trusted" : "Add to trusted"}
                                                    title={isProviderTrusted ? "In your trusted roster" : "Add to trusted roster"}
                                                >
                                                    <Heart
                                                        size={13}
                                                        strokeWidth={1.5}
                                                        fill={isProviderTrusted ? "currentColor" : "none"}
                                                    />
                                                </button>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <div className="overline">Total</div>
                                            <div className="font-serif text-2xl">
                                                <span className="gold-text">$</span>
                                                {b.total_amount.toLocaleString()}
                                                <span className="text-xs text-muted-foreground ml-1">{b.currency}</span>
                                            </div>
                                        </div>
                                        {!["completed", "cancelled"].includes(b.status) && (
                                            <Button
                                                variant="outline"
                                                onClick={() => handleCancel(b.id)}
                                                className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 px-4 bg-transparent border-white/10 text-muted-foreground hover:text-red-300 hover:border-red-700/40"
                                                data-testid={`cancel-btn-${b.id}`}
                                            >
                                                Cancel
                                            </Button>
                                        )}
                                        {(isCompleted || b.status === "cancelled") && (
                                            <Button
                                                variant="outline"
                                                onClick={() => handleBookAgain(b)}
                                                className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 px-4 bg-transparent border-primary/30 text-primary hover:bg-primary/10"
                                                data-testid={`book-again-${b.id}`}
                                            >
                                                Book again
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline"
                                            onClick={() => navigate(`/bookings/${b.id}`)}
                                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 px-4 bg-transparent border-primary/30 text-primary hover:bg-primary/10"
                                            data-testid={`open-chat-${b.id}`}
                                        >
                                            Open chat
                                        </Button>
                                        {isCompleted && (
                                            <BookingActions
                                                booking={b}
                                                canReview={canReview}
                                                onReviewed={fetchData}
                                                onTipped={fetchData}
                                            />
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}

                {/* Royal Referrals — earn SMART Coins for every friend invited */}
                <div className="mt-12">
                    <RoyalReferralCard />
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Stat({ label, value, icon }) {
    return (
        <div className="border-l border-white/10 pl-4">
            <div className="overline mb-1 flex items-center gap-2">
                {icon} {label}
            </div>
            <div className="font-serif text-3xl">{value}</div>
        </div>
    );
}

function BookingActions({ booking, canReview, onReviewed, onTipped }) {
    const [showReview, setShowReview] = useState(false);
    const [showTip, setShowTip] = useState(false);
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [tipAmount, setTipAmount] = useState(1000);
    const [busy, setBusy] = useState(false);

    const submitReview = async () => {
        setBusy(true);
        try {
            await api.post(`/bookings/${booking.id}/review`, { rating, comment });
            toast.success("Review submitted ★");
            setShowReview(false);
            onReviewed?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const submitTip = async () => {
        setBusy(true);
        try {
            await api.post(`/bookings/${booking.id}/tip`, { amount: parseFloat(tipAmount) });
            toast.success(`Tip of $${tipAmount} added — your pro will receive it in next payout.`);
            setShowTip(false);
            onTipped?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-col items-end gap-2 w-full" data-testid={`booking-actions-${booking.id}`}>
            {booking.rating ? (
                <div className="flex items-center gap-1 text-xs">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                            key={`booking-star-${n}`}
                            size={12}
                            strokeWidth={1.5}
                            fill={n <= booking.rating ? "currentColor" : "none"}
                            className={n <= booking.rating ? "gold-text" : "text-muted-foreground"}
                        />
                    ))}
                    <span className="ml-1 text-muted-foreground text-[10px] uppercase tracking-[0.2em]">your review</span>
                </div>
            ) : canReview && (
                <Button
                    size="sm"
                    onClick={() => setShowReview((v) => !v)}
                    className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 px-4 bg-transparent border border-primary/40 text-primary hover:bg-primary/10"
                    data-testid={`review-btn-${booking.id}`}
                >
                    <Star size={12} strokeWidth={1.5} className="mr-1.5" /> Rate
                </Button>
            )}
            {booking.tip_amount > 0 && (
                <div className="text-[10px] uppercase tracking-[0.2em] gold-text">
                    Tipped ${booking.tip_amount.toLocaleString()}
                </div>
            )}
            {booking.provider_id && (
                <Button
                    size="sm"
                    onClick={() => setShowTip((v) => !v)}
                    className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 px-4 bg-transparent border border-white/10 text-muted-foreground hover:text-primary hover:border-primary/40"
                    data-testid={`tip-btn-${booking.id}`}
                >
                    <DollarSign size={12} strokeWidth={1.5} className="mr-1.5" /> Tip
                </Button>
            )}

            {showReview && (
                <div className="w-full max-w-xs p-4 bg-background border border-primary/30 rounded-sm space-y-3" data-testid={`review-form-${booking.id}`}>
                    <div className="overline">Rate your pro</div>
                    <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button
                                key={n}
                                onClick={() => setRating(n)}
                                className="p-1"
                                data-testid={`star-${n}`}
                                aria-label={`${n} stars`}
                            >
                                <Star
                                    size={20}
                                    strokeWidth={1.5}
                                    fill={n <= rating ? "currentColor" : "none"}
                                    className={n <= rating ? "gold-text" : "text-muted-foreground"}
                                />
                            </button>
                        ))}
                    </div>
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                        placeholder="Optional comment..."
                        className="w-full bg-input border border-white/10 rounded-sm p-2 text-xs"
                        data-testid={`review-comment-${booking.id}`}
                    />
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            onClick={submitReview}
                            disabled={busy}
                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-primary text-primary-foreground"
                            data-testid={`review-submit-${booking.id}`}
                        >
                            Submit
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowReview(false)}
                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 text-muted-foreground"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            {showTip && (
                <div className="w-full max-w-xs p-4 bg-background border border-primary/30 rounded-sm space-y-3" data-testid={`tip-form-${booking.id}`}>
                    <div className="overline">Tip your pro (JMD)</div>
                    <div className="flex gap-2">
                        {[500, 1000, 2000, 5000].map((amt) => (
                            <button
                                key={amt}
                                onClick={() => setTipAmount(amt)}
                                className={`flex-1 py-2 text-xs border rounded-sm ${
                                    tipAmount === amt
                                        ? "border-primary text-primary bg-primary/10"
                                        : "border-white/10 text-muted-foreground"
                                }`}
                                data-testid={`tip-amt-${amt}`}
                            >
                                ${amt.toLocaleString()}
                            </button>
                        ))}
                    </div>
                    <input
                        type="number"
                        value={tipAmount}
                        onChange={(e) => setTipAmount(e.target.value)}
                        className="w-full bg-input border border-white/10 rounded-sm p-2 text-sm"
                        data-testid={`tip-input-${booking.id}`}
                    />
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            onClick={submitTip}
                            disabled={busy}
                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-primary text-primary-foreground"
                            data-testid={`tip-submit-${booking.id}`}
                        >
                            Send Tip
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowTip(false)}
                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 text-muted-foreground"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
